<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref, watch } from "vue";
import {
    evaluatePassword,
    getPasswordPolicyConfig,
    passwordMessages,
} from "@/utils/passwordPolicy";
import { getCsrfToken } from "@/api/utils";

type UncompromisedStatus = "idle" | "checking" | "valid" | "invalid";

const props = defineProps<{
    password?: string;
    passwordInputId?: string;
    confirmInputId?: string;
    submitSelector?: string;
    showConfirmMatch?: boolean;
}>();

const emit = defineEmits<{
    (event: "update:valid", value: boolean): void;
    (event: "update:errors", value: string[]): void;
}>();

const config = getPasswordPolicyConfig();
const localPassword = ref(props.password ?? "");
const confirmPassword = ref("");
const uncompromisedStatus = ref<UncompromisedStatus>("idle");
const uncompromisedMessage = ref<string | null>(null);
const debounceId = ref<number | null>(null);
let abortController: AbortController | null = null;
let passwordInput: HTMLInputElement | null = null;
let confirmInput: HTMLInputElement | null = null;
let submitButton: HTMLButtonElement | null = null;

const syncFromDom = () => {
    if (passwordInput) {
        localPassword.value = passwordInput.value ?? "";
    }

    if (confirmInput) {
        confirmPassword.value = confirmInput.value ?? "";
    }
};

const attachDomListeners = () => {
    if (props.passwordInputId) {
        passwordInput = document.getElementById(
            props.passwordInputId,
        ) as HTMLInputElement | null;
    }

    if (props.confirmInputId) {
        confirmInput = document.getElementById(
            props.confirmInputId,
        ) as HTMLInputElement | null;
    }

    if (props.submitSelector) {
        submitButton = document.querySelector(
            props.submitSelector,
        ) as HTMLButtonElement | null;
    }

    if (passwordInput) {
        passwordInput.addEventListener("input", syncFromDom);
    }

    if (confirmInput) {
        confirmInput.addEventListener("input", syncFromDom);
    }

    syncFromDom();
};

const detachDomListeners = () => {
    if (passwordInput) {
        passwordInput.removeEventListener("input", syncFromDom);
    }

    if (confirmInput) {
        confirmInput.removeEventListener("input", syncFromDom);
    }
};

const baseChecks = computed(() =>
    evaluatePassword(localPassword.value, config),
);

const shouldCheckUncompromised = computed(() => {
    if (!config.enforceUncompromised) {
        return false;
    }

    return baseChecks.value.isValid;
});

const confirmMatchRequired = computed(() => {
    return Boolean(props.showConfirmMatch || props.confirmInputId);
});

const confirmMatches = computed(() => {
    if (!confirmMatchRequired.value) {
        return true;
    }

    if (confirmPassword.value.length === 0) {
        return false;
    }

    return localPassword.value === confirmPassword.value;
});

const displayChecks = computed(() => {
    const checks = [...baseChecks.value.checks];

    if (confirmMatchRequired.value) {
        checks.push({
            key: "confirm",
            label: "Passwords match.",
            satisfied: confirmMatches.value,
        });
    }

    if (config.enforceUncompromised) {
        const label = "Password is not found in a known data breach.";
        checks.push({
            key: "uncompromised",
            label,
            satisfied: uncompromisedStatus.value === "valid",
            pending: uncompromisedStatus.value === "checking",
        } as {
            key: string;
            label: string;
            satisfied: boolean;
            pending?: boolean;
        });
    }

    return checks;
});

const errors = computed(() => {
    const currentErrors = baseChecks.value.checks
        .filter((check) => !check.satisfied)
        .map((check) => check.label);

    if (confirmMatchRequired.value && !confirmMatches.value) {
        currentErrors.push("Passwords do not match.");
    }

    if (uncompromisedStatus.value === "invalid") {
        currentErrors.push(
            uncompromisedMessage.value ?? passwordMessages.uncompromised,
        );
    }

    return currentErrors;
});

const isValid = computed(() => {
    if (!baseChecks.value.isValid) {
        return false;
    }

    if (confirmMatchRequired.value && !confirmMatches.value) {
        return false;
    }

    if (config.enforceUncompromised) {
        return uncompromisedStatus.value === "valid";
    }

    return true;
});

const runUncompromisedCheck = async () => {
    if (!config.enforceUncompromised) {
        uncompromisedStatus.value = "valid";
        return;
    }

    if (!shouldCheckUncompromised.value) {
        uncompromisedStatus.value = "idle";
        uncompromisedMessage.value = null;
        return;
    }

    if (!localPassword.value) {
        uncompromisedStatus.value = "idle";
        uncompromisedMessage.value = null;
        return;
    }

    abortController?.abort();
    abortController = new AbortController();
    uncompromisedStatus.value = "checking";
    uncompromisedMessage.value = null;

    try {
        const response = await fetch("/password/policy/check", {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                "X-CSRF-TOKEN": getCsrfToken(),
            },
            body: JSON.stringify({ password: localPassword.value }),
            signal: abortController.signal,
        });

        if (response.ok) {
            uncompromisedStatus.value = "valid";
            return;
        }

        if (response.status === 422) {
            const data = await response.json();
            uncompromisedStatus.value = "invalid";
            uncompromisedMessage.value =
                data?.errors?.password?.[0] ?? passwordMessages.uncompromised;
            return;
        }

        uncompromisedStatus.value = "invalid";
        uncompromisedMessage.value = passwordMessages.uncompromised;
    } catch (error) {
        if ((error as Error)?.name === "AbortError") {
            return;
        }

        uncompromisedStatus.value = "invalid";
        uncompromisedMessage.value = passwordMessages.uncompromised;
    }
};

const scheduleUncompromisedCheck = () => {
    if (!config.enforceUncompromised) {
        uncompromisedStatus.value = "valid";
        return;
    }

    if (debounceId.value) {
        window.clearTimeout(debounceId.value);
    }

    debounceId.value = window.setTimeout(() => {
        runUncompromisedCheck();
    }, 350);
};

watch(
    () => props.password,
    (value) => {
        if (typeof value === "string") {
            localPassword.value = value;
        }
    },
);

watch(localPassword, () => {
    scheduleUncompromisedCheck();
});

watch([isValid, errors], () => {
    emit("update:valid", isValid.value);
    emit("update:errors", errors.value);

    // Leave the button disabled while prevent-double-submit has the form in flight
    if (submitButton && !submitButton.form?.dataset.submitting) {
        submitButton.disabled = !isValid.value;
    }
});

onMounted(() => {
    attachDomListeners();
    scheduleUncompromisedCheck();

    if (submitButton) {
        submitButton.disabled = !isValid.value;
    }
});

onBeforeUnmount(() => {
    detachDomListeners();
    abortController?.abort();

    if (debounceId.value) {
        window.clearTimeout(debounceId.value);
    }
});
</script>

<template>
    <div class="space-y-2 text-xs text-gray-600">
        <p class="font-medium text-gray-700">Password requirements</p>
        <ul class="space-y-1">
            <li
                v-for="check in displayChecks"
                :key="check.key"
                :class="{
                    'text-green-700': check.satisfied,
                    'text-gray-500': !check.satisfied,
                }"
            >
                <span class="mr-1">
                    {{
                        check.satisfied
                            ? "[ok]"
                            : check.pending
                              ? "[...]"
                              : "[ ]"
                    }}
                </span>
                {{ check.label }}
            </li>
        </ul>
    </div>
</template>
